import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Premium full-width product slideshow — Coca-Cola / Red Bull quality.
 * - 10 slides, each a single product with its own branded background
 * - Autoplay every 5s · 0.7s fade transitions
 * - Pause on hover, pause when tab inactive, pause on reduced-motion preference
 * - Previous/next arrows, dot indicators, swipe on mobile
 * - Progress bar underlining the current slide
 * - Images lazy-loaded, aspect-ratio reserved to prevent CLS
 */

const AUTOPLAY_MS = 5000;
const TRANSITION_MS = 700;

const SLIDES = [
  {
    id: "onecola-original", brandSlug: "onecola", name: "OneCola Original", pack: "PET bottle · aluminium can",
    description: "Discover the bold, refreshing taste of OneCola Original. A sparkling cola experience created for moments of enjoyment, refreshment and togetherness.",
    cta: "DISCOVER ONECOLA", accent: "#DC2626",
    bg: "linear-gradient(135deg, #1a0505 0%, #450a0a 55%, #0a0a0a 100%)",
    image: "https://customer-assets-jai6qajn.emergentagent.net/job_brew-excellence-2/artifacts/c71cac57b1e182d1_OneCola%20Cola%20Drink%20Product%20Showcase.png",
  },
  {
    id: "maltora-original", brandSlug: "maltora", name: "Maltora Original", pack: "250ml · 330ml · 500ml cans · 330ml glass",
    description: "Experience the rich, satisfying character of Maltora, our premium non-alcoholic malt drink, created to deliver distinctive malt flavour and refreshing enjoyment.",
    cta: "DISCOVER MALTORA", accent: "#C2410C",
    bg: "linear-gradient(135deg, #78350F 0%, #431407 55%, #1c0a05 100%)",
    image: "https://customer-assets-jai6qajn.emergentagent.net/job_brew-excellence-2/artifacts/f623a65f071fedae_Maltora%20Premium%20Non-Alcoholic%20Malt%20Drink%20Showcase.png",
  },
  {
    id: "voltx-original", brandSlug: "voltx", name: "VoltX Energy", pack: "250ml · 330ml · 500ml cans · PET bottle",
    description: "Power your moments with VoltX, an exciting energy drink designed for a bold and dynamic lifestyle.",
    cta: "DISCOVER VOLTX", accent: "#2563EB",
    bg: "linear-gradient(135deg, #0a0a0a 0%, #0b2a66 50%, #050a1f 100%)",
    image: "https://customer-assets-jai6qajn.emergentagent.net/job_brew-excellence-2/artifacts/d6f37277dad5d7f6_VoltX%20Electric%20Blue%20Energy%20Lineup.png",
  },
  {
    id: "onefresh-water", brandSlug: "onefresh", name: "OneFresh Drinking Water", pack: "330ml · 500ml · 1L",
    description: "Enjoy simple, refreshing hydration with OneFresh. Created for everyday refreshment, whether at home, at work or on the move.",
    cta: "DISCOVER ONEFRESH WATER", accent: "#0EA5E9",
    bg: "linear-gradient(135deg, #082F49 0%, #0C4A6E 50%, #0a0a0a 100%)",
    image: "https://customer-assets-jai6qajn.emergentagent.net/job_brew-excellence-2/artifacts/252e87cf1cb2da95_OneFresh%20Hydration%20Range%20in%20Nature.png",
  },
  {
    id: "onefresh-orange", brandSlug: "onefresh", name: "OneFresh Orange Juice", pack: "1.5L carton",
    description: "Bright, refreshing orange flavour in every serving. OneFresh Orange brings a vibrant citrus experience to your everyday refreshment.",
    cta: "DISCOVER ORANGE", accent: "#F97316",
    bg: "linear-gradient(135deg, #431407 0%, #9A3412 55%, #0a0a0a 100%)",
    image: "https://customer-assets-jai6qajn.emergentagent.net/job_brew-excellence-2/artifacts/b1c2b844621ae7d0_OneFresh%20Orange%20Juice%20Orchard%20Glow.png",
  },
  {
    id: "onefresh-apple", brandSlug: "onefresh", name: "OneFresh Apple Juice", pack: "1.5L carton",
    description: "Enjoy the crisp, refreshing taste of apples with OneFresh Apple Juice, a delicious fruit drink for everyday enjoyment.",
    cta: "DISCOVER APPLE", accent: "#DC2626",
    bg: "linear-gradient(135deg, #052e16 0%, #166534 55%, #0a0a0a 100%)",
    image: "https://customer-assets-jai6qajn.emergentagent.net/job_brew-excellence-2/artifacts/773bde72a4e0788d_OneFresh%20Apple%20Juice%20Orchard%20Refreshment.png",
  },
  {
    id: "onefresh-pineapple", brandSlug: "onefresh", name: "OneFresh Pineapple Juice", pack: "1.5L carton",
    description: "Bring a taste of the tropics to your day with the bright, refreshing pineapple flavour of OneFresh Pineapple Juice.",
    cta: "DISCOVER PINEAPPLE", accent: "#EAB308",
    bg: "linear-gradient(135deg, #713F12 0%, #CA8A04 55%, #0a0a0a 100%)",
    image: "https://customer-assets-jai6qajn.emergentagent.net/job_brew-excellence-2/artifacts/99f7d60942fe92fc_OneFresh%20Pineapple%20Juice%20Tropical%20Still%20Life.png",
  },
  {
    id: "onefresh-mango", brandSlug: "onefresh", name: "OneFresh Mango Juice", pack: "1.5L carton",
    description: "Indulge in the smooth, tropical character of OneFresh Mango Juice, bursting with delicious mango-inspired flavour.",
    cta: "DISCOVER MANGO", accent: "#F59E0B",
    bg: "linear-gradient(135deg, #78350F 0%, #D97706 55%, #0a0a0a 100%)",
    image: "https://customer-assets-jai6qajn.emergentagent.net/job_brew-excellence-2/artifacts/cf39b14cb6c9c2e1_OneFresh%20Mango%20Juice%20Tropical%20Product%20Shot.png",
  },
  {
    id: "onefresh-cranberry", brandSlug: "onefresh", name: "OneFresh Cranberry Juice", pack: "1.5L carton",
    description: "Discover the distinctive, refreshing cranberry flavour of OneFresh Cranberry Juice, a vibrant addition to our fruit juice collection.",
    cta: "DISCOVER CRANBERRY", accent: "#BE123C",
    bg: "linear-gradient(135deg, #4C0519 0%, #9F1239 55%, #0a0a0a 100%)",
    image: "https://customer-assets-jai6qajn.emergentagent.net/job_brew-excellence-2/artifacts/c56df2cf68167616_OneFresh%20Cranberry%20Juice%20Splash.png",
  },
  {
    id: "onefresh-mixed", brandSlug: "onefresh", name: "OneFresh Mixed Fruits Juice", pack: "1.5L carton",
    description: "Enjoy a delicious combination of fruity flavours with OneFresh Mixed Fruits Juice, bringing a colourful and refreshing experience to every serving.",
    cta: "DISCOVER MIXED FRUITS", accent: "#DB2777",
    bg: "linear-gradient(135deg, #500724 0%, #9D174D 55%, #0a0a0a 100%)",
    image: "https://customer-assets-jai6qajn.emergentagent.net/job_brew-excellence-2/artifacts/464195c39ee2e442_OneFresh%20Tropical%20Mixed%20Fruits%20Juice.png",
  },
];

export default function ProductSlideshow() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const prefersReducedMotion = useMemo(
    () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
    []
  );
  const touchStart = useRef(null);

  const go = useCallback((n) => setIndex((i) => (i + n + SLIDES.length) % SLIDES.length), []);
  const goTo = (n) => setIndex(((n % SLIDES.length) + SLIDES.length) % SLIDES.length);

  // Autoplay — paused on hover, when tab inactive, or when user prefers reduced motion
  useEffect(() => {
    if (paused || prefersReducedMotion) return;
    const id = setInterval(() => go(1), AUTOPLAY_MS);
    return () => clearInterval(id);
  }, [paused, go, prefersReducedMotion]);

  // Pause autoplay when tab hidden
  useEffect(() => {
    const onVis = () => setPaused(document.hidden);
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  // Swipe
  const onTouchStart = (e) => { touchStart.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStart.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
    touchStart.current = null;
  };

  const slide = SLIDES[index];

  return (
    <section
      className="relative overflow-hidden bg-neutral-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      aria-roledescription="carousel"
      aria-label="SBC Ltd product showcase"
      data-testid="product-slideshow"
    >
      <div className="relative h-[640px] sm:h-[560px] md:h-[600px] lg:h-[640px]">
        <AnimatePresence mode="sync">
          <motion.div
            key={slide.id}
            className="absolute inset-0"
            initial={{ opacity: 0, scale: prefersReducedMotion ? 1 : 1.02 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: TRANSITION_MS / 1000, ease: [0.4, 0, 0.2, 1] }}
            style={{ background: slide.bg }}
            role="group"
            aria-roledescription="slide"
            aria-label={`${index + 1} of ${SLIDES.length}: ${slide.name}`}
          >
            {/* Grain + ambient glow */}
            <div className="absolute inset-0 opacity-[0.06] mix-blend-overlay pointer-events-none"
                 style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")" }} />
            <div className="absolute -top-32 -left-20 w-[500px] h-[500px] rounded-full blur-3xl opacity-30 pointer-events-none"
                 style={{ background: `radial-gradient(circle, ${slide.accent}66 0%, transparent 70%)` }} />
            <div className="absolute -bottom-24 -right-24 w-[500px] h-[500px] rounded-full blur-3xl opacity-40 pointer-events-none"
                 style={{ background: `radial-gradient(circle, ${slide.accent}44 0%, transparent 70%)` }} />

            {/* Content grid */}
            <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 items-center gap-6 lg:gap-12 pt-12 pb-16 lg:py-0 text-white">
              {/* Left — copy */}
              <motion.div
                initial={{ opacity: 0, x: prefersReducedMotion ? 0 : -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2, duration: 0.8 }}
                className="order-2 lg:order-1 max-w-xl"
              >
                <div className="text-[11px] uppercase tracking-[0.3em] font-semibold mb-3" style={{ color: slide.accent }}>
                  Product Showcase · {index + 1} / {SLIDES.length}
                </div>
                <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[1.02]">
                  {slide.name}
                </h2>
                <div className="mt-3 text-sm uppercase tracking-widest text-white/60">{slide.pack}</div>
                <p className="mt-5 text-base lg:text-lg leading-relaxed text-white/85">{slide.description}</p>
                <Link
                  to={`/brands/${slide.brandSlug}`}
                  className="mt-7 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold uppercase tracking-widest transition-all hover:brightness-110"
                  style={{ background: slide.accent, color: "#0a0a0a" }}
                  data-testid={`slide-cta-${slide.id}`}
                >
                  {slide.cta} <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>

              {/* Right — product image */}
              <motion.div
                initial={{ opacity: 0, scale: prefersReducedMotion ? 1 : 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1, duration: 1 }}
                className="order-1 lg:order-2 relative aspect-[4/3] lg:aspect-auto lg:h-[450px]"
              >
                <img
                  src={slide.image}
                  alt={slide.name}
                  loading="lazy"
                  className="w-full h-full object-contain drop-shadow-[0_30px_60px_rgba(0,0,0,0.5)]"
                  data-testid={`slide-image-${slide.id}`}
                />
              </motion.div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Prev/Next arrows */}
        <button
          onClick={() => go(-1)}
          className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/40 hover:bg-black/70 backdrop-blur-md border border-white/15 flex items-center justify-center text-white transition-all z-10"
          aria-label="Previous product"
          data-testid="slideshow-prev"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          onClick={() => go(1)}
          className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/40 hover:bg-black/70 backdrop-blur-md border border-white/15 flex items-center justify-center text-white transition-all z-10"
          aria-label="Next product"
          data-testid="slideshow-next"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Dots indicator */}
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-10" role="tablist" aria-label="Choose product">
        {SLIDES.map((s, i) => (
          <button
            key={s.id}
            onClick={() => goTo(i)}
            className="group p-1"
            role="tab"
            aria-selected={i === index}
            aria-label={s.name}
            data-testid={`slideshow-dot-${i}`}
          >
            <span className={`block h-1 rounded-full transition-all duration-500 ${i === index ? "w-8 bg-white" : "w-2 bg-white/40 group-hover:bg-white/70"}`} />
          </button>
        ))}
      </div>
    </section>
  );
}
